import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, CircleSlash } from 'lucide-react';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDisplayDateTime } from '@/utils/date';
import { pcmpStatusCatalogQueryOptions } from '../../api/pcmpStatus.api';
import { PCMP_MIS_DECISION_COPY } from '../../constants/pcmpMis.constants';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { useDecidePcmpProposalMis } from '../../hooks/useDecidePcmpProposalMis';
import type { PcmpMisDecision } from '../../types/pcmpProposal.types';
import type { PcmpMisNotVerified } from '../../types/pcmpQueue.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const decisionFormSchema = z
  .object({
    decision: z.enum(['VERIFIED', 'NOT_VERIFIED']),
    reason: z.string().optional(),
    comment: z.string().trim().min(1, 'A comment is required.').max(PCMP_MKT_COMMENT_MAX),
  })
  .superRefine((values, ctx) => {
    if (values.decision === 'NOT_VERIFIED' && !values.reason) {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Select a sub-status.' });
    }
  });

type DecisionFormValues = z.infer<typeof decisionFormSchema>;

export interface PcmpMisDecisionTarget {
  proposalId: number;
  refNo: string;
  decision: PcmpMisDecision;
  /** The earlier Not Verified round, when the caller has it. */
  notVerified?: PcmpMisNotVerified | null | undefined;
}

interface PcmpMisDecisionDialogProps {
  target: PcmpMisDecisionTarget;
  onClose: () => void;
}

/** Legacy MisVerify: Verify, or Not Verified with a sub-status. A comment is always required. */
export function PcmpMisDecisionDialog({ target, onClose }: PcmpMisDecisionDialogProps) {
  const { decision, notVerified } = target;
  const copy = PCMP_MIS_DECISION_COPY[decision];
  const form = useForm<DecisionFormValues>({
    resolver: zodResolver(decisionFormSchema),
    defaultValues: { decision, reason: undefined, comment: '' },
  });
  const { mutate, isPending, error } = useDecidePcmpProposalMis(
    target.proposalId,
    decision,
    onClose,
  );
  const { data: catalog, isLoading: catalogLoading } = useQuery({
    ...pcmpStatusCatalogQueryOptions(),
    enabled: decision === 'NOT_VERIFIED',
  });
  const reasons = catalog?.misReasons ?? [];

  const onSubmit = form.handleSubmit((values) =>
    mutate({
      decision,
      reason: decision === 'NOT_VERIFIED' ? values.reason : undefined,
      comment: values.comment,
    }),
  );

  const explanation = notVerified?.explanation;

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={copy.title}
        description={target.refNo}
        icon={decision === 'VERIFIED' ? CircleCheck : CircleSlash}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={copy.label}
        submitVariant={decision === 'VERIFIED' ? 'default' : 'destructive'}
        pendingLabel="Saving..."
      >
        {notVerified ? (
          <div className="flex flex-col gap-1.5 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
            <span className="text-xs text-muted-foreground">Earlier marked Not Verified</span>
            <span className="font-medium">{notVerified.reasonLabel ?? 'Not Verified'}</span>
            {explanation ? (
              <>
                <span className="mt-1 text-xs text-muted-foreground">
                  Marketing&apos;s explanation
                  {explanation.by ? ` · ${explanation.by.fullName}` : ''}
                  {explanation.at ? ` · ${formatDisplayDateTime(explanation.at)}` : ''}
                </span>
                <span className="whitespace-pre-line">{explanation.text}</span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">
                Marketing has not explained it yet.
              </span>
            )}
          </div>
        ) : null}

        {decision === 'NOT_VERIFIED' ? (
          <>
            <p className="text-sm text-muted-foreground">
              Marketing is asked to explain. A new decision clears any earlier explanation round.
            </p>
            <FormField
              control={form.control}
              name="reason"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>
                    Sub-status <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select<string>
                    items={reasons.map((reason) => ({ value: reason.code, label: reason.label }))}
                    value={field.value ?? null}
                    onValueChange={(next) => field.onChange(next ?? undefined)}
                    disabled={catalogLoading}
                  >
                    <FormControl>
                      <SelectTrigger
                        className="w-full"
                        aria-invalid={fieldState.invalid || undefined}
                      >
                        <SelectValue
                          placeholder={catalogLoading ? 'Loading...' : 'Select a sub-status'}
                        />
                        <SelectIcon />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {reasons.map((reason) => (
                        <SelectItem key={reason.code} value={reason.code}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : null}

        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required
          placeholder={decision === 'VERIFIED' ? 'What was checked' : 'What did not check out'}
        />
      </PcmpEditDialog>
    </Form>
  );
}
